import { useMemo, useState } from "react";
import { discussions } from "@/mocks/homeFeed";
import { hotTopics, contributors } from "@/mocks/homeMeta";
import SectionHeading from "./SectionHeading";

interface Post {
  id: string;
  author: string;
  initial: string;
  tag: string;
  content: string;
  time: string;
  likes: number;
  replies: number;
}

export default function DiscussionFeed() {
  const [posts, setPosts] = useState<Post[]>(discussions);
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [draft, setDraft] = useState("");

  const maxCount = useMemo(
    () => hotTopics.reduce((max, topic) => Math.max(max, topic.count), 1),
    [],
  );

  const toggleLike = (id: string) => {
    setLiked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const publish = () => {
    const text = draft.trim();
    if (!text) return;
    setPosts((prev) => [
      {
        id: `local-${Date.now()}`,
        author: "我",
        initial: "我",
        tag: "参与讨论",
        content: text,
        time: "刚刚",
        likes: 0,
        replies: 0,
      },
      ...prev,
    ]);
    setDraft("");
  };

  return (
    <section id="discussions" className="w-full bg-background-100 py-14 md:py-20">
      <div className="w-full px-4 md:px-6">
        <SectionHeading
          index="06"
          eyebrow="Community"
          title="社区讨论：吃瓜的人也在帮忙对时间线"
          desc="这里不吵立场，只比证据。有人补原始出处，有人整理逐字稿，也有人单纯提问，思路清楚再下场。"
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <div className="rounded-lg bg-background-50 border border-background-200 p-4 md:p-5">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-full bg-primary-100 text-primary-800 font-heading font-bold flex items-center justify-center shrink-0">
                  我
                </span>
                <div className="flex-1">
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={2}
                    placeholder="说说你手上的线索，或者想核实的疑点…"
                    className="w-full resize-none bg-background-100 border border-background-200 rounded-md px-3 py-2.5 text-sm font-label text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                  />
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs font-label text-foreground-400">
                      请附上可核验的原始出处，便于他人对照
                    </span>
                    <button
                      type="button"
                      onClick={publish}
                      className="h-9 px-4 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label transition-colors whitespace-nowrap cursor-pointer"
                    >
                      发布
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {posts.map((post) => {
                const isLiked = Boolean(liked[post.id]);
                return (
                  <article
                    key={post.id}
                    className="rounded-lg bg-background-50 border border-background-200 hover:border-background-300 transition-colors p-4 md:p-5"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 rounded-full bg-secondary-100 text-secondary-800 font-heading font-bold flex items-center justify-center shrink-0">
                        {post.initial}
                      </span>
                      <div className="min-w-0">
                        <div className="font-heading text-sm font-semibold text-foreground-950">
                          {post.author}
                        </div>
                        <div className="text-xs font-label text-foreground-500 mt-0.5">
                          {post.time}
                        </div>
                      </div>
                      <span className="ml-auto shrink-0 text-[11px] font-label text-foreground-700 bg-background-100 border border-background-200 rounded-full px-2.5 py-1 whitespace-nowrap">
                        {post.tag}
                      </span>
                    </div>
                    <p className="mt-3.5 text-sm text-foreground-700 leading-relaxed">
                      {post.content}
                    </p>
                    <div className="mt-4 flex items-center gap-5 text-xs font-label text-foreground-500">
                      <button
                        type="button"
                        onClick={() => toggleLike(post.id)}
                        className={`inline-flex items-center gap-1.5 transition-colors whitespace-nowrap cursor-pointer ${
                          isLiked ? "text-primary-600" : "hover:text-primary-600"
                        }`}
                      >
                        <i
                          className={`${isLiked ? "ri-heart-fill" : "ri-heart-line"} text-sm w-4 h-4 flex items-center justify-center`}
                        />
                        {post.likes + (isLiked ? 1 : 0)}
                      </button>
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <i className="ri-chat-3-line text-sm w-4 h-4 flex items-center justify-center" />
                        {post.replies}
                      </span>
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-share-forward-line text-sm w-4 h-4 flex items-center justify-center" />
                        分享对照
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-4 space-y-4">
            <div className="rounded-lg bg-background-50 border border-background-200 p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-950 flex items-center gap-2">
                <i className="ri-fire-line text-primary-600 text-lg w-5 h-5 flex items-center justify-center" />
                正在热议的疑点
              </h3>
              <ul className="mt-4 space-y-3.5">
                {hotTopics.map((topic) => (
                  <li key={topic.label}>
                    <div className="flex items-center justify-between text-sm font-label text-foreground-800">
                      <span className="truncate pr-3">{topic.label}</span>
                      <span className="text-foreground-400 text-xs shrink-0">
                        {topic.count}
                      </span>
                    </div>
                    <div className="mt-2 h-1.5 w-full rounded-full bg-background-200 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-secondary-500"
                        style={{ width: `${Math.round((topic.count / maxCount) * 100)}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-background-50 border border-background-200 p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-950 flex items-center gap-2">
                <i className="ri-medal-line text-accent-600 text-lg w-5 h-5 flex items-center justify-center" />
                时间线贡献者
              </h3>
              <ul className="mt-4 space-y-3.5">
                {contributors.map((person) => (
                  <li key={person.name} className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-accent-100 text-accent-800 font-heading font-bold text-sm flex items-center justify-center shrink-0">
                      {person.initial}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-heading text-sm font-semibold text-foreground-950 truncate">
                        {person.name}
                      </div>
                      <div className="text-xs font-label text-foreground-500 mt-0.5">
                        {person.badge}
                      </div>
                    </div>
                    <span className="text-xs font-label text-foreground-500 shrink-0">
                      {person.posts} 条
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}